/**
 * BaseApiClient — shared plumbing for the admin API client (CUR-547 split).
 *
 * Every per-domain API module (patterns-api, channels-api, newsletters-api,
 * articles-api, videos-api, …) extends this class so its methods can use
 * `this.baseUrl` / `this.resolveHeaders()` / `this.fetchJSON()`. The domain
 * classes are never instantiated directly — their prototype methods are
 * merged onto AdminAPIClient in client.ts via applyMixins, preserving the
 * original flat `apiClient.method()` surface.
 *
 * Mixin constraint: domain classes must be METHODS-ONLY. Instance state
 * (baseUrl, token getter) lives here, because applyMixins copies prototype
 * methods but never runs field initializers or constructors.
 */

import { buildApiUrl, processApiResponse, createAuthHeaders, withRetry, DEFAULT_RETRY_OPTIONS, APIError as SharedAPIError } from '../../../../shared/utils/api-helpers';
import { getCurrentOrgId, getIsImpersonating } from '@/contexts/OrgContext';
import { transformKeysToCamelCase } from '@/utils/transform-keys';

/**
 * Token getter type - supports both sync and async token retrieval.
 * Sync: For internal JWT tokens stored in localStorage
 * Async: For Auth0 tokens that require getAccessTokenSilently()
 */
export type TokenGetter = () => string | null | Promise<string | null>;

export class BaseApiClient {
  protected baseUrl: string;
  private getToken: TokenGetter;

  constructor(baseUrl: string, getToken: TokenGetter) {
    this.baseUrl = baseUrl;
    this.getToken = getToken;
  }

  /**
   * Get auth token, handling both sync and async token getters.
   */
  protected async resolveToken(): Promise<string | null> {
    const tokenOrPromise = this.getToken();
    if (tokenOrPromise instanceof Promise) {
      return await tokenOrPromise;
    }
    return tokenOrPromise;
  }

  /**
   * Build auth headers with optional org impersonation context.
   *
   * When a super admin is impersonating an org via the portal org switcher,
   * includes the X-Organization-ID header so the backend resolves the
   * correct org context instead of defaulting to the super admin's JWT org.
   */
  protected async resolveHeaders(): Promise<Record<string, string>> {
    const headers = createAuthHeaders(await this.resolveToken());
    const orgId = getCurrentOrgId();
    const isImpersonating = getIsImpersonating();
    if (isImpersonating && orgId) {
      headers['X-Organization-ID'] = orgId;
    }
    return headers;
  }

  /**
   * Generic JSON fetch with auth headers, error handling, snake→camel transform, and retry.
   *
   * Used by unified hooks that need to hit different URL prefixes (/api/* vs /portal/*)
   * while reusing the same auth + transform + retry pipeline.
   */
  async fetchJSON<T>(path: string, options?: { method?: string; body?: unknown; transformKeys?: boolean; accept?: string }): Promise<T> {
    const url = buildApiUrl(this.baseUrl, path);
    const headers = await this.resolveHeaders();

    const doFetch = async () => {
      const mergedHeaders: Record<string, string> = { ...headers };
      if (options?.body) {
        mergedHeaders['Content-Type'] = 'application/json';
      }
      if (options?.accept) {
        mergedHeaders['Accept'] = options.accept;
      }
      const response = await fetch(url, {
        method: options?.method || 'GET',
        headers: mergedHeaders,
        body: options?.body ? JSON.stringify(options.body) : undefined,
      });

      const result = await processApiResponse<unknown>(response);
      if (result.error) {
        throw new SharedAPIError(result.status, result.error.message, result.error.code);
      }
      if (!result.data) {
        throw new SharedAPIError(500, 'No data returned');
      }
      if (options?.transformKeys === false) {
        return result.data as T;
      }
      return transformKeysToCamelCase<T>(result.data);
    };

    return withRetry(doFetch, DEFAULT_RETRY_OPTIONS);
  }
}
